import { rpx } from 'client/lib/rpx-client';
import { useEffect, useState } from 'preact/hooks';
import { showError } from '@components/app-error';
import dayjs from 'dayjs';

type Note = Awaited<ReturnType<typeof rpx.memberNotes.list>>['notes'][number];

export function MemberNotesList(props: { memberUserId: UUID }) {
  const [notes, setNotes] = useState<Note[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  async function refresh() {
    try {
      const result = await rpx.memberNotes.list({ userId: props.memberUserId });
      setNotes(result.notes);
    } catch (err) {
      showError(err);
    } finally {
      setIsLoading(false);
    }
  }

  useEffect(() => {
    refresh();
  }, [props.memberUserId]);

  return (
    <NotesPanel
      notes={notes}
      isLoading={isLoading}
      memberUserId={props.memberUserId}
      onChange={refresh}
    />
  );
}

export function NotesPanel(props: {
  memberUserId: UUID;
  notes: Note[];
  isLoading: boolean;
  onChange: () => Promise<void>;
}) {
  const [draft, setDraft] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  async function addNote(e: Event) {
    e.preventDefault();
    const body = draft.trim();
    if (!body) {
      return;
    }
    setIsSaving(true);
    try {
      await rpx.memberNotes.create({ userId: props.memberUserId, body });
      setDraft('');
      await props.onChange();
    } catch (err) {
      showError(err);
    } finally {
      setIsSaving(false);
    }
  }

  return (
    <div class="flex flex-col gap-3">
      <form class="flex flex-col gap-2" onSubmit={addNote}>
        <textarea
          class="w-full rounded border border-gray-300 px-2 py-1 text-sm placeholder-gray-400 focus:border-indigo-500 focus:outline-none"
          rows={2}
          value={draft}
          placeholder="Add context that future helpers will want…"
          onInput={(e) => setDraft((e.target as HTMLTextAreaElement).value)}
          disabled={isSaving}
        />
        <div class="flex items-center justify-between">
          <p class="text-xs text-gray-500">Editable for 24 hours after posting.</p>
          <button
            type="submit"
            class="rounded bg-indigo-600 px-3 py-1 text-xs font-medium text-white hover:bg-indigo-700 disabled:opacity-50"
            disabled={isSaving || !draft.trim()}
          >
            {isSaving ? 'Saving…' : 'Add note'}
          </button>
        </div>
      </form>
      {props.isLoading ? (
        <p class="text-sm text-gray-500">Loading notes…</p>
      ) : props.notes.length === 0 ? (
        <p class="text-sm text-gray-500">No notes yet.</p>
      ) : (
        <ul class="flex flex-col gap-2">
          {props.notes.map((n) => (
            <NoteItem key={n.id} note={n} onChange={props.onChange} />
          ))}
        </ul>
      )}
    </div>
  );
}

function NoteItem(props: { note: Note; onChange: () => Promise<void> }) {
  const { note } = props;
  const [isEditing, setIsEditing] = useState(false);
  const [draft, setDraft] = useState(note.body);
  const [isSaving, setIsSaving] = useState(false);

  async function save() {
    const body = draft.trim();
    if (!body) {
      return;
    }
    setIsSaving(true);
    try {
      await rpx.memberNotes.update({ noteId: note.id, body });
      setIsEditing(false);
      await props.onChange();
    } catch (err) {
      if (isEditWindowClosed(err)) {
        alert('This note can no longer be edited (24 hours have passed).');
        setIsEditing(false);
        await props.onChange();
      } else {
        showError(err);
      }
    } finally {
      setIsSaving(false);
    }
  }

  async function remove() {
    if (!confirm('Delete this note? This cannot be undone.')) {
      return;
    }
    setIsSaving(true);
    try {
      await rpx.memberNotes.remove({ noteId: note.id });
      await props.onChange();
    } catch (err) {
      if (isEditWindowClosed(err)) {
        alert('This note can no longer be deleted (24 hours have passed).');
        await props.onChange();
      } else {
        showError(err);
      }
    } finally {
      setIsSaving(false);
    }
  }

  const editedAt = note.updatedAt ? new Date(note.updatedAt).getTime() : 0;
  const createdAt = note.createdAt ? new Date(note.createdAt).getTime() : 0;
  const wasEdited = editedAt > 0 && createdAt > 0 && editedAt - createdAt > 60 * 1000;
  return (
    <li class="rounded border border-gray-200 bg-white p-2 text-sm">
      <div class="flex items-baseline justify-between">
        <span class="text-xs font-medium text-gray-700">
          {note.authorName || note.authorEmail || 'Unknown'}
        </span>
        <span class="text-xs text-gray-500">
          {dayjs(note.createdAt).format('MMM D, YYYY · h:mm A')}
          {wasEdited && ' · edited'}
        </span>
      </div>
      {isEditing ? (
        <div class="mt-2 flex flex-col gap-2">
          <textarea
            class="w-full rounded border border-gray-300 px-2 py-1 text-sm focus:border-indigo-500 focus:outline-none"
            rows={3}
            value={draft}
            onInput={(e) => setDraft((e.target as HTMLTextAreaElement).value)}
            disabled={isSaving}
          />
          <div class="flex items-center gap-2">
            <button
              type="button"
              class="rounded bg-indigo-600 px-2 py-1 text-xs font-medium text-white hover:bg-indigo-700 disabled:opacity-50"
              onClick={save}
              disabled={isSaving || !draft.trim()}
            >
              {isSaving ? 'Saving…' : 'Save'}
            </button>
            <button
              type="button"
              class="text-xs text-gray-600 hover:underline"
              onClick={() => {
                setDraft(note.body);
                setIsEditing(false);
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      ) : (
        <>
          <p class="mt-1 whitespace-pre-wrap text-sm text-gray-700">{note.body}</p>
          {note.isEditable && (
            <div class="mt-1 flex items-center gap-3">
              <button
                type="button"
                class="text-xs text-indigo-700 hover:underline"
                onClick={() => setIsEditing(true)}
              >
                Edit
              </button>
              <button
                type="button"
                class="text-xs text-red-700 hover:underline disabled:opacity-50"
                onClick={remove}
                disabled={isSaving}
              >
                Delete
              </button>
            </div>
          )}
        </>
      )}
    </li>
  );
}

function isEditWindowClosed(err: unknown): boolean {
  const status = err as { statusCode?: number } | undefined;
  return status?.statusCode === 403;
}
